/**
 * Clipboard em memória do compositor split — compartilhado entre banners
 * da mesma página (copiar estilo/elemento de um hero para outro).
 */

let clipboard = null
const listeners = new Set()

export function getSplitClipboard() {
  return clipboard
}

export function setSplitClipboard(next) {
  clipboard = next
  listeners.forEach((fn) => {
    try {
      fn(clipboard)
    } catch {
      /* ignore */
    }
  })
  return clipboard
}

export function clearSplitClipboard() {
  return setSplitClipboard(null)
}

export function subscribeSplitClipboard(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Extrai snapshot de posicionamento do placement desktop/mobile. */
export function snapshotPlacementKeys(style, keys) {
  const pick = (place) => {
    const out = {}
    keys.forEach((k) => {
      if (place && place[k] != null) out[k] = place[k]
    })
    return out
  }
  return {
    desktop: pick(style.desktop),
    mobile: pick(style.mobile),
  }
}

export function applyPlacementKeys(style, snap, keys) {
  if (!snap) return style
  const merge = (place, from) => {
    if (!from) return place
    const next = { ...place }
    keys.forEach((k) => {
      if (from[k] != null) next[k] = from[k]
    })
    return next
  }
  return {
    ...style,
    desktop: merge(style.desktop, snap.desktop),
    mobile: merge(style.mobile, snap.mobile),
  }
}

export const PRODUCT_KEYS = ['x', 'y', 'scale', 'rotate', 'height']
export const META_POS_KEYS = ['meta_x', 'meta_y', 'meta_width', 'meta_rotate']
export const PRICE_POS_KEYS = ['price_x', 'price_y']
export const CTA_POS_KEYS = ['cta_x', 'cta_y', 'cta_w', 'cta_h']

export function captureElement(kind, style, block = null) {
  if (kind === 'product') {
    return {
      mode: 'full',
      kind: 'product',
      snapshot: snapshotPlacementKeys(style, PRODUCT_KEYS),
    }
  }
  if (kind === 'meta') {
    return {
      mode: 'full',
      kind: 'meta',
      snapshot: {
        ...snapshotPlacementKeys(style, META_POS_KEYS),
        font_family: style.font_family,
        font_size_desktop: style.font_size_desktop,
        font_size_mobile: style.font_size_mobile,
      },
    }
  }
  if (kind === 'price') {
    return {
      mode: 'full',
      kind: 'price',
      snapshot: {
        ...snapshotPlacementKeys(style, PRICE_POS_KEYS),
        price_font_family: style.price_font_family,
        price_font_size_desktop: style.price_font_size_desktop,
        price_font_size_mobile: style.price_font_size_mobile,
        price_color: style.price_color,
      },
    }
  }
  if (kind === 'cta') {
    return {
      mode: 'full',
      kind: 'cta',
      snapshot: {
        ...snapshotPlacementKeys(style, CTA_POS_KEYS),
        cta_font_family: style.cta_font_family,
        cta_font_size_desktop: style.cta_font_size_desktop,
        cta_font_size_mobile: style.cta_font_size_mobile,
        cta_color: style.cta_color,
        cta_bg_color: style.cta_bg_color,
        cta_radius: style.cta_radius,
        cta_appearance: style.cta_appearance,
      },
    }
  }
  if (kind === 'block' && block) {
    return {
      mode: 'full',
      kind: 'text_block',
      snapshot: { ...block },
    }
  }
  return null
}

export function captureStyle(kind, style, block = null) {
  if (kind === 'product') {
    return {
      mode: 'style',
      kind: 'product',
      snapshot: snapshotPlacementKeys(style, ['scale', 'rotate', 'height']),
    }
  }
  if (kind === 'meta') {
    return {
      mode: 'style',
      kind: 'meta',
      snapshot: {
        font_family: style.font_family,
        font_size_desktop: style.font_size_desktop,
        font_size_mobile: style.font_size_mobile,
      },
    }
  }
  if (kind === 'price') {
    return {
      mode: 'style',
      kind: 'price',
      snapshot: {
        price_font_family: style.price_font_family,
        price_font_size_desktop: style.price_font_size_desktop,
        price_font_size_mobile: style.price_font_size_mobile,
        price_color: style.price_color,
      },
    }
  }
  if (kind === 'cta') {
    return {
      mode: 'style',
      kind: 'cta',
      snapshot: {
        cta_font_family: style.cta_font_family,
        cta_font_size_desktop: style.cta_font_size_desktop,
        cta_font_size_mobile: style.cta_font_size_mobile,
        cta_color: style.cta_color,
        cta_bg_color: style.cta_bg_color,
        cta_radius: style.cta_radius,
        cta_appearance: style.cta_appearance,
      },
    }
  }
  if (kind === 'block' && block) {
    return {
      mode: 'style',
      kind: 'text_block',
      snapshot: {
        font_family: block.font_family,
        font_size_desktop: block.font_size_desktop,
        font_size_mobile: block.font_size_mobile,
        color: block.color,
      },
    }
  }
  return null
}

/**
 * Aplica clipboard sobre o style atual.
 * Retorna { style, selectBlockId } ou null se incompatível.
 */
export function applyClipboard(clip, style, selection) {
  if (!clip?.snapshot || !selection) return null
  const { kind: selKind, blockId } = selection
  const snap = clip.snapshot
  const full = clip.mode === 'full'

  // Texto → texto (colar elemento = duplicar com novo id; estilo = só tipografia)
  if (clip.kind === 'text_block' && selKind === 'block') {
    if (!full) {
      return {
        style: {
          ...style,
          text_blocks: (style.text_blocks || []).map((b) =>
            b.id === blockId
              ? {
                  ...b,
                  font_family: snap.font_family ?? b.font_family,
                  font_size_desktop:
                    snap.font_size_desktop ?? b.font_size_desktop,
                  font_size_mobile: snap.font_size_mobile ?? b.font_size_mobile,
                  color: snap.color ?? b.color,
                }
              : b
          ),
        },
      }
    }
    // Full paste onto existing block: replace content+style+pos
    return {
      style: {
        ...style,
        text_blocks: (style.text_blocks || []).map((b) =>
          b.id === blockId
            ? {
                ...b,
                text: snap.text ?? b.text,
                font_family: snap.font_family ?? b.font_family,
                font_size_desktop: snap.font_size_desktop ?? b.font_size_desktop,
                font_size_mobile: snap.font_size_mobile ?? b.font_size_mobile,
                color: snap.color ?? b.color,
                desktop: snap.desktop ? { ...snap.desktop } : b.desktop,
                mobile: snap.mobile ? { ...snap.mobile } : b.mobile,
                show_desktop: snap.show_desktop !== false,
                show_mobile: snap.show_mobile !== false,
              }
            : b
        ),
      },
    }
  }

  if (clip.kind === 'product' && selKind === 'product') {
    const keys = full ? PRODUCT_KEYS : ['scale', 'rotate', 'height']
    return { style: applyPlacementKeys(style, snap, keys) }
  }

  if (clip.kind === 'meta' && selKind === 'meta') {
    let next = { ...style }
    if (full) next = applyPlacementKeys(next, snap, META_POS_KEYS)
    next = {
      ...next,
      font_family: snap.font_family ?? next.font_family,
      font_size_desktop: snap.font_size_desktop ?? next.font_size_desktop,
      font_size_mobile: snap.font_size_mobile ?? next.font_size_mobile,
      show_product_name: true,
    }
    return { style: next }
  }

  if (clip.kind === 'price' && selKind === 'price') {
    let next = { ...style }
    if (full) next = applyPlacementKeys(next, snap, PRICE_POS_KEYS)
    next = {
      ...next,
      price_font_family: snap.price_font_family ?? next.price_font_family,
      price_font_size_desktop:
        snap.price_font_size_desktop ?? next.price_font_size_desktop,
      price_font_size_mobile:
        snap.price_font_size_mobile ?? next.price_font_size_mobile,
      price_color: snap.price_color ?? next.price_color,
      show_product_price: true,
    }
    return { style: next }
  }

  if (clip.kind === 'cta' && selKind === 'cta') {
    let next = { ...style }
    if (full) next = applyPlacementKeys(next, snap, CTA_POS_KEYS)
    next = {
      ...next,
      cta_font_family: snap.cta_font_family ?? next.cta_font_family,
      cta_font_size_desktop:
        snap.cta_font_size_desktop ?? next.cta_font_size_desktop,
      cta_font_size_mobile:
        snap.cta_font_size_mobile ?? next.cta_font_size_mobile,
      cta_color: snap.cta_color ?? next.cta_color,
      cta_bg_color: snap.cta_bg_color ?? next.cta_bg_color,
      cta_radius: snap.cta_radius ?? next.cta_radius,
      cta_appearance: snap.cta_appearance ?? next.cta_appearance,
      show_cta: true,
    }
    return { style: next }
  }

  // Colar bloco de texto como novo (clipboard full + seleção vazia ou outro tipo)
  if (clip.kind === 'text_block' && full && clip.mode === 'full') {
    return { style: null, insertTextBlock: snap }
  }

  return null
}
